import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showErrorToast } from "aldoo-ra/Toast"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import { UserAddresses, AddAddress, UpdateAddress, RemoveAddress, SetDefaultAddress } from "../storeApi"
import AddressForm, { addressLine } from "./AddressForm"
import { Section, Card, Loading, ErrorText } from "./ui"

/** Address book (user {address list|add|update|remove|setDefault}). */
export default function Addresses() {
  const t = useT()
  const { result, pending, refresh } = useAsync(() => UserAddresses(), [])
  const [editing, setEditing] = useState(null) // null | "new" | address
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const addresses = [...(result?.addresses || [])].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))

  const run = async (fn) => {
    setBusy(true)
    setError(null)
    try {
      const response = await fn()
      if (response?.error) setError(t(response.error))
      else setEditing(null)
      refresh()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const remove = (address) =>
    showPromptDialog({
      title: t`Delete Address`,
      message: t`Are you sure you want to delete this address? This action cannot be undone.`,
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
      onConfirm: () =>
        run(async () => {
          const response = await RemoveAddress(address._id)
          if (response?.error) showErrorToast(t`Failed to delete address. Please try again.`)
          return response
        }),
    })

  return (
    <Section
      title={t`Shipping Addresses`}
      actions={
        !editing && (
          <Button variant="primary" onClick={() => setEditing("new")} data-testid="address-add">
            {t`Add Address`}
          </Button>
        )
      }
    >
      <ErrorText>{error}</ErrorText>
      {editing && (
        <Card>
          <AddressForm initial={editing === "new" ? undefined : editing} busy={busy} onCancel={() => setEditing(null)} onSubmit={(address) => run(() => (editing === "new" ? AddAddress(address) : UpdateAddress(editing._id, address)))} />
        </Card>
      )}
      {pending && !result && <Loading />}
      {result && addresses.length === 0 && !editing && (
        <div className="py-16 text-center">
          <p className="m-0 text-[20px] text-text_secondary">{t`No addresses saved`}</p>
          <p className="m-0 mt-4 text-[14px] text-text_secondary">{t`Add a shipping address to make checkout faster.`}</p>
        </div>
      )}
      <div className="flex flex-col gap-3" data-testid="address-list">
        {addresses.map((address) => (
          <Card key={address._id} className="flex flex-row flex-wrap items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="font-semibold">
                {address.name}
                {address.isDefault && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase text-primary">{t`Primary Address`}</span>}
              </span>
              <span className="text-[14px] text-text_secondary">{addressLine({ ...address, name: undefined })}</span>
            </div>
            <div className="flex gap-4">
              {!address.isDefault && (
                <Button variant="text_btn_ghost" onClick={() => run(() => SetDefaultAddress(address._id))}>
                  {t`Set as Primary`}
                </Button>
              )}
              <Button variant="text_btn_ghost" onClick={() => setEditing(address)}>
                {t`Edit`}
              </Button>
              {/* the primary address cannot be deleted (old addresses.jsx) */}
              <Button variant="text_btn_ghost" onClick={() => remove(address)} disabled={address.isDefault} title={address.isDefault ? t`Primary Address` : t`Delete`}>
                {t`Delete`}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </Section>
  )
}
